import * as React from "react";
import { Bell, CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { MonoValue } from "@/components/redesign";
import { cn } from "@/lib/utils";

export type NotificationTone = "error" | "warning" | "success" | "brand" | "neutral";

export interface NotificationItem {
  id: string;
  title: string;
  description?: string;
  meta?: string;
  tone?: NotificationTone;
  icon?: React.ReactNode;
  isRead?: boolean;
  onClick?: () => void;
}

const toneIconBg: Record<NotificationTone, string> = {
  error: "bg-error-50 text-error-600",
  warning: "bg-warning-50 text-warning-600",
  success: "bg-success-50 text-success-600",
  brand: "bg-brand-50 text-brand-600",
  neutral: "bg-gray-100 text-gray-600",
};

interface NotificationsBellProps {
  items?: NotificationItem[];
  className?: string;
  /** Tailwind text color for the bell icon (matches the surrounding header). */
  iconClassName?: string;
}

/** Bell trigger + slide-in notifications panel ("Bildirimler menusu"). */
export const NotificationsBell: React.FC<NotificationsBellProps> = ({
  items = [],
  className,
  iconClassName,
}) => {
  const { t } = useTranslation();
  const unreadCount = items.filter((item) => !item.isRead).length;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={t("notifications_inbox.title", { defaultValue: "Bildirimler" })}
          className={cn("relative", className)}
        >
          <Bell className={cn("h-5 w-5", iconClassName)} />
          {unreadCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-error-600 px-1 text-[10px] font-bold leading-none text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[92%] bg-gray-25 p-0 sm:max-w-md">
        <SheetHeader className="bg-brand-900 px-5 py-4 text-left">
          <SheetTitle className="text-white">
            {t("notifications_inbox.title", { defaultValue: "Bildirimler" })}
          </SheetTitle>
          <p className="text-[12px] text-brand-25">
            {unreadCount > 0
              ? t("notifications_inbox.unread_count", {
                  count: unreadCount,
                  defaultValue: `${unreadCount} okunmamış bildirim`,
                })
              : t("notifications_inbox.all_read", { defaultValue: "Tümü okundu" })}
          </p>
        </SheetHeader>

        <div className="flex max-h-[calc(100%-72px)] flex-col gap-2 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
              {t("notifications_inbox.empty", { defaultValue: "Yeni bildirim yok" })}
            </div>
          ) : (
            items.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={item.onClick}
                className={cn(
                  "flex w-full items-start gap-3 rounded-2xl border p-3 text-left shadow-sm transition-colors",
                  item.isRead
                    ? "border-gray-200 bg-white/80"
                    : "border-brand-200 bg-white",
                )}
              >
                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                    toneIconBg[item.tone ?? "brand"],
                  )}
                >
                  {item.icon ?? <Bell className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      "text-[14px] text-gray-900",
                      item.isRead ? "font-medium" : "font-semibold",
                    )}
                  >
                    {item.title}
                  </p>
                  {item.description && (
                    <p className="truncate text-[12px] text-gray-600">{item.description}</p>
                  )}
                </div>
                {item.meta && (
                  <MonoValue value={item.meta} size="meta" className="shrink-0 text-gray-500" />
                )}
              </button>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/** Maps upcoming/overdue check payments to notification items. */
export function checksToNotifications(
  checks: Array<{
    id: string | number;
    dueInDays?: number;
    projectName?: string;
    description?: string;
    amount?: number;
  }> = [],
  t: (key: string, opts?: Record<string, unknown>) => string,
): NotificationItem[] {
  return (checks || []).slice(0, 20).map((c) => {
    const overdue = typeof c.dueInDays === "number" && c.dueInDays < 0;
    const today = c.dueInDays === 0;
    return {
      id: String(c.id),
      title: overdue
        ? t("notifications_inbox.payment_overdue", { defaultValue: "Ödeme gecikti" })
        : today
          ? t("notifications_inbox.payment_today", { defaultValue: "Ödeme bugün" })
          : t("notifications_inbox.payment_upcoming", { defaultValue: "Yaklaşan ödeme" }),
      description: [c.projectName, c.description].filter(Boolean).join(" • "),
      meta:
        typeof c.amount === "number"
          ? `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(c.amount)} ₺`
          : undefined,
      tone: overdue || today ? "error" : "warning",
      icon: <CalendarClock className="h-4 w-4" />,
      isRead: false,
    };
  });
}

export default NotificationsBell;
